import React, { useMemo, useState } from 'react';
import { Box, costFormater, NewDonutChart, RawButton, Text } from '@common-fe/common-fe';
import _ from 'lodash';

import theme from '@/styles/theme';

import {
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  DEFAULT_VISIBLE_CATEGORIES,
} from '../EmployerServiceCategories.constants';
import { ChartListItem, ClaimBreakdownCategoryPayload } from '../EmployerServiceCategories.types';

import ChartDescriptionItems from './ChartDescriptionItems';

interface Props {
  categories: ClaimBreakdownCategoryPayload[];
  totalPaidClaims: number;
  titleNode?: React.ReactNode;
}
const OTHER_TITLE = 'Other';
const MIN_DISPLAYED_PERCENTS = 0;
const ServiceCategoriesDonut: React.FC<Props> = ({ categories, totalPaidClaims, titleNode }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const donutData = useMemo(() => {
    const filteredItems = categories.filter((item) => item.percentage >= MIN_DISPLAYED_PERCENTS);
    const items = filteredItems.map((item) => ({
      key: item.categoryType,
      color: CATEGORY_COLORS[item.categoryType] || theme.colors.iconDisabled,
      value: item.percentage,
      hint: (
        <Box pad="spacing8" gap="spacing4">
          <Text size="small" weight={700} color="textOnColor">
            {CATEGORY_LABELS[item.categoryType] || item.categoryType || OTHER_TITLE}
          </Text>
          <Text size="small" color="textOnColor">
            {costFormater(item.amount, { isPrecision: true })} ({_.round(item.percentage, 2)}%)
          </Text>
        </Box>
      ),
    }));
    return items;
  }, [categories]);

  const visibleCategories = useMemo<ChartListItem[]>(() => {
    const formatted = categories.map(
      (item): ChartListItem => ({
        id: item.categoryType,
        color: CATEGORY_COLORS[item.categoryType] || theme.colors.iconDisabled,
        title: CATEGORY_LABELS[item.categoryType] || item.categoryType || OTHER_TITLE,
        percentage: categories?.length === 1 ? 100 : item.percentage,
        value: item.amount,
      })
    );
    if (isExpanded || categories.length <= DEFAULT_VISIBLE_CATEGORIES) {
      return formatted;
    }
    return formatted.slice(0, DEFAULT_VISIBLE_CATEGORIES);
  }, [categories, isExpanded]);

  const showToggle = categories?.length > DEFAULT_VISIBLE_CATEGORIES;

  return (
    <Box fill direction="column">
      <Box direction="row" justify="between" align="center" height="20px">
        <Text size="medium" weight={700}>
          Service categories
        </Text>
        {titleNode}
      </Box>

      <Box direction="row" align="stretch" margin={{ top: 'spacing16' }}>
        <Box flex={false}>
          <Box width="152px" height="152px" style={{ position: 'relative' }}>
            <Box style={{ position: 'absolute', top: '-8px' }}>
              <NewDonutChart data={donutData} size={170} strokeWidth={19} testId="service-categories-donut">
                <Box align="center" justify="center">
                  <Text size="large" weight={700}>
                    {totalPaidClaims}
                  </Text>
                  <Text size="small" color="textSecondary">
                    Paid claims
                  </Text>
                </Box>
              </NewDonutChart>
            </Box>
          </Box>
        </Box>

        <Box direction="column" width="100%" margin={{ left: 'spacing8' }}>
          <ChartDescriptionItems items={visibleCategories} testId="service-categories" />

          {showToggle && (
            <RawButton
              onClick={() => setIsExpanded(!isExpanded)}
              data-testid="show-all-categories-toggle"
              style={{ lineHeight: 1 }}
            >
              <Box pad={{ top: 'spacing8' }}>
                <Text size="medium" color="textAccent" weight={400}>
                  {isExpanded ? 'Hide' : 'Show all'}
                </Text>
              </Box>
            </RawButton>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ServiceCategoriesDonut;
